import { useEffect, useRef } from 'react';

import {
  mergeSelectedPoolDscrMetrics,
  transformSelectedPoolsData,
  usePortfolioContext,
  type SelectablePool,
  type SelectedPoolData,
  type SelectedPoolDscrMetrics,
} from '../PortfolioContext';
import { useSelectedPoolDscr } from '../gql/getSelectedPoolDscr_gql';

type SyncSelectedPoolsOptions = {
  /** When true, DSCR comes only from `dscrMetrics` (e.g. page already fetched performance_summary). */
  skipLazyFetch?: boolean;
  dscrMetrics?: SelectedPoolDscrMetrics | null;
};

const poolSyncToken = (pool: SelectablePool): string =>
  [
    pool.id,
    pool.name,
    pool.t_current_balance_cents,
    pool.loan_count,
    pool.wa_loan_age_months,
    pool.wa_remaining_loan_terms_months,
    pool.wa_coupon_bps,
    pool.wa_borrower_credit_score,
    pool.wa_ltv_bps,
    pool.wa_dti_bps,
  ].join(':');

const getPoolsSyncKey = (pools: SelectablePool[] | null | undefined): string =>
  pools?.length ? pools.map(poolSyncToken).join('|') : '';

const getDscrSyncKey = (dscr: SelectedPoolDscrMetrics | null | undefined): string =>
  dscr
    ? [
        dscr.minDscr,
        dscr.maxDscr,
        dscr.avgDscr,
        dscr.dscrCount,
        dscr.nonQmCount,
        dscr.dscrPct,
        dscr.nonQmPct,
        // Asset-util counts ride the same summary (ENG-3391); omitting them
        // here would keep Total in Pool stale when only these change (e.g.
        // null → real counts once the analytics deploy lands).
        dscr.assetUtilCount,
        dscr.assetUtilPct,
      ].join(':')
    : '';

const buildSelectedPoolsPayload = (
  pools: SelectablePool[],
  dscr: SelectedPoolDscrMetrics | null,
): SelectedPoolData[] => {
  const base = transformSelectedPoolsData(pools);

  if (dscr && base[0]) {
    return [mergeSelectedPoolDscrMetrics(base[0], dscr), ...base.slice(1)];
  }

  return base;
};

/**
 * Syncs base pool list fields into portfolio context immediately, then lazily
 * fetches DSCR metrics for the primary selected pool (one Lambda invoke per
 * selection, not per list poll).
 */
export const useSyncSelectedPoolsData = (
  selectedPools: SelectablePool[] | null | undefined,
  options?: SyncSelectedPoolsOptions,
) => {
  const { setSelectedPoolsData } = usePortfolioContext();
  const primaryPoolId = selectedPools?.[0]?.id ?? null;
  const skipLazyFetch = options?.skipLazyFetch ?? false;
  const { data: dscrData } = useSelectedPoolDscr(skipLazyFetch ? null : primaryPoolId);

  const poolsRef = useRef(selectedPools);
  poolsRef.current = selectedPools;

  const dscrSummary = skipLazyFetch
    ? (options?.dscrMetrics ?? null)
    : (dscrData?.user?.company?.pool?.performance_summary ?? null);
  const dscrRef = useRef(dscrSummary);
  dscrRef.current = dscrSummary;

  const poolsSyncKey = getPoolsSyncKey(selectedPools);
  const dscrSyncKey = getDscrSyncKey(dscrSummary);
  const lastPayloadRef = useRef<string | null>(null);

  useEffect(() => {
    const pools = poolsRef.current;

    if (!pools?.length) {
      if (lastPayloadRef.current === '[]') {
        return;
      }

      lastPayloadRef.current = '[]';
      setSelectedPoolsData([]);
      return;
    }

    const payload = buildSelectedPoolsPayload(pools, dscrRef.current);
    const payloadKey = JSON.stringify(payload);

    if (payloadKey === lastPayloadRef.current) {
      return;
    }

    lastPayloadRef.current = payloadKey;
    setSelectedPoolsData(payload);
  }, [poolsSyncKey, dscrSyncKey, skipLazyFetch, setSelectedPoolsData]);
};
